import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { MessageCircle, Send, Settings, Trash2, Book, FileText, ChevronDown, ExternalLink, Plus, Menu } from 'lucide-react';
import toast from 'react-hot-toast';
import AuthContext from '../contexts/AuthContext';
import UpgradePrompt from '../components/UpgradePrompt';

const ChatPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [mode, setMode] = useState('auto');
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [selectedMaterialId, setSelectedMaterialId] = useState('');
  const [courses, setCourses] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [materialSearchQuery, setMaterialSearchQuery] = useState('');
  const [showModeSelector, setShowModeSelector] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(sessionId);
  const [showSidebar, setShowSidebar] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const initializePage = async () => {
      // Always stop loading first
      setIsLoadingHistory(false);
      
      // Load sessions (but don't block the page if it fails)
      loadSessions();
      loadCourses();
      checkSubscription();
      
      // If we have a sessionId, load that session's history
      if (sessionId) {
        loadChatHistory(sessionId);
      } else {
        setMessages([]);
      }
    };
    
    initializePage();
  }, []);

  useEffect(() => {
    if (sessionId !== currentSessionId) {
      setCurrentSessionId(sessionId);
      if (sessionId) {
        loadChatHistory(sessionId);
      } else {
        setMessages([]);
        setIsLoadingHistory(false);
      }
    }
  }, [sessionId, currentSessionId]);

  const loadSessions = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/chat/sessions`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions || []);
      } else {
        // If sessions endpoint fails (maybe tables don't exist yet), just use empty array
        console.warn('Sessions endpoint failed, using empty sessions list');
        setSessions([]);
      }
    } catch (error) {
      // If there's a network error or database issue, don't block the page
      console.warn('Could not load sessions, using empty list:', error.message);
      setSessions([]);
    }
  };

  const checkSubscription = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/subscriptions/current`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        const subscriptionData = await response.json();
        setSubscription(subscriptionData);
      } else {
        // If subscription check fails, assume free plan
        console.warn('Subscription check failed, assuming free plan');
        setSubscription({ planId: 'free', aiAccess: false });
      }
    } catch (error) {
      console.warn('Could not check subscription status:', error.message);
      setSubscription({ planId: 'free', aiAccess: false });
    }
  };

  const loadChatHistory = async (sessionIdParam = null) => {
    try {
      setIsLoadingHistory(true);
      const targetSessionId = sessionIdParam || currentSessionId;
      
      if (!targetSessionId) {
        setMessages([]);
        setIsLoadingHistory(false);
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/chat/history?sessionId=${targetSessionId}&limit=50`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        const formattedHistory = data.history.flatMap(item => [
          {
            id: `${item.id}_user`,
            content: item.message,
            sender: 'user',
            timestamp: item.timestamp
          },
          {
            id: `${item.id}_ai`,
            content: item.response,
            sender: 'ai',
            timestamp: item.timestamp,
            contextSources: item.contextSources
          }
        ]);
        
        setMessages(formattedHistory);
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const loadCourses = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/chat/courses`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setCourses(data.courses);
      }
    } catch (error) {
      console.error('Error loading courses:', error);
    }
  };

  const searchMaterials = async (query) => {
    if (query.length < 2) {
      setMaterials([]);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/chat/materials/search?q=${encodeURIComponent(query)}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setMaterials(data.materials);
      }
    } catch (error) {
      console.error('Error searching materials:', error);
    }
  };

  const createNewChat = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/chat/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ title: 'New Chat' })
      });

      if (response.ok) {
        const newSession = await response.json();
        navigate(`/chat/${newSession.sessionId}`);
        loadSessions(); // Refresh sessions list
        toast.success('New chat created');
      } else {
        // Fallback: just navigate to main chat page
        navigate('/chat');
        setMessages([]);
        toast.success('New chat started');
      }
    } catch (error) {
      console.error('Error creating new chat:', error);
      // Fallback: just navigate to main chat page
      navigate('/chat');
      setMessages([]);
      toast.success('New chat started');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!inputMessage.trim() || isLoading) return;

    // Check if user has Pro subscription for AI features
    if (subscription && !subscription.aiAccess) {
      setShowUpgradePrompt(true);
      return;
    }

    const userMessage = {
      id: Date.now(),
      content: inputMessage,
      sender: 'user',
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const requestBody = {
        message: inputMessage,
        mode: mode,
        sessionId: currentSessionId
      };

      if (mode === 'course' && selectedCourseId) {
        requestBody.courseId = selectedCourseId;
      } else if (mode === 'material' && selectedMaterialId) {
        requestBody.materialId = selectedMaterialId;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send message');
      }

      const data = await response.json();
      
      // If this was a new chat (no session), navigate to the new session
      if (!currentSessionId && data.sessionId) {
        navigate(`/chat/${data.sessionId}`);
        loadSessions(); // Refresh sessions list
        return; // The useEffect will handle loading the new session
      }
      
      // If this was the first message in an existing session, refresh sessions to get updated title
      if (currentSessionId && messages.length === 1) {
        loadSessions();
      }
      
      const aiMessage = {
        id: Date.now() + 1,
        content: data.response,
        sender: 'ai',
        timestamp: data.timestamp,
        contextSources: data.contextSources
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(error.message || 'Failed to send message');
      
      // Add error message to chat
      const errorMessage = {
        id: Date.now() + 1,
        content: 'Sorry, I encountered an error processing your message. Please try again.',
        sender: 'ai',
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const clearHistory = async () => {
    if (!currentSessionId) {
      setMessages([]);
      return;
    }

    const confirmClear = window.confirm('Are you sure you want to clear this chat? This action cannot be undone.');
    if (!confirmClear) return;

    try {
      // For now, just clear the messages locally since we don't have a delete session endpoint yet
      setMessages([]);
      toast.success('Chat cleared');
      
      // Navigate to main chat page
      navigate('/chat');
    } catch (error) {
      console.error('Error clearing history:', error);
      toast.error('Failed to clear chat');
    }
  };

  const getModeDescription = () => {
    switch (mode) {
      case 'course':
        const selectedCourse = courses.find(c => c.id === selectedCourseId);
        return selectedCourse ? `Course: ${selectedCourse.code} - ${selectedCourse.name}` : 'Select a course';
      case 'material':
        const selectedMaterial = materials.find(m => m.id === selectedMaterialId);
        return selectedMaterial ? `Material: ${selectedMaterial.title}` : 'Search and select a material';
      default:
        return 'All subscribed courses';
    }
  };

  if (isLoadingHistory) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
      </div>
    );
  }

  return (
    <div className="flex h-full max-h-screen bg-primary text-primary">
      {/* Sidebar */}
      <div className={`${showSidebar ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 fixed md:relative z-30 flex flex-col w-64 h-full bg-secondary border-r border-gray-200 dark:border-gray-700 transition-transform duration-300 ease-in-out`}>
        {/* Sidebar Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2">
            <MessageCircle size={20} className="text-red-600 dark:text-red-400" />
            <h2 className="font-semibold text-primary">Lexi Chats</h2>
          </div>
          <button
            onClick={createNewChat}
            className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            title="New Chat"
          >
            <Plus size={18} />
          </button>
        </div>

        {/* Sessions List */}
        <div className="flex-1 overflow-y-auto p-2">
          <div className="space-y-1">
            {sessions.map((session) => (
              <Link
                key={session.id}
                to={`/chat/${session.id}`}
                className={`block p-3 rounded-lg transition-colors group ${
                  session.id === currentSessionId
                    ? 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-secondary'
                }`}
              >
                <div className="text-sm font-medium truncate">
                  {session.title}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {new Date(session.lastMessageAt).toLocaleDateString()}
                </div>
              </Link>
            ))}
            {sessions.length === 0 && (
              <div className="text-center text-secondary py-8">
                <MessageCircle size={32} className="mx-auto mb-2 text-gray-400" />
                <p className="text-sm">No chats yet</p>
                <p className="text-xs">Start a new conversation!</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Overlay for mobile */}
      {showSidebar && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden"
          onClick={() => setShowSidebar(false)}
        />
      )}

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col max-h-screen">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-secondary">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowSidebar(!showSidebar)}
              className="md:hidden p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            >
              <Menu size={20} />
            </button>
            <div className="p-2 bg-red-100 dark:bg-red-900 rounded-full">
              <MessageCircle size={24} className="text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-primary">Lexi AI Assistant</h1>
              <p className="text-sm text-secondary">Your intelligent course companion</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={clearHistory}
              className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
              title="Clear current chat"
            >
              <Trash2 size={18} />
            </button>
          </div>
        </div>

      {/* Mode Selector */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-secondary">
        <div className="relative">
          <button
            onClick={() => setShowModeSelector(!showModeSelector)}
            className="flex items-center justify-between w-full p-3 bg-primary border border-gray-300 dark:border-gray-600 rounded-lg text-primary hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-2">
                {mode === 'course' && <Book size={16} />}
                {mode === 'material' && <FileText size={16} />}
                {mode === 'auto' && <MessageCircle size={16} />}
                <span className="font-medium">{mode === 'auto' ? 'Auto Mode' : mode === 'course' ? 'Course Mode' : 'Material Mode'}</span>
              </div>
              <span className="text-sm text-secondary">- {getModeDescription()}</span>
            </div>
            <ChevronDown size={16} className={`transform transition-transform ${showModeSelector ? 'rotate-180' : ''}`} />
          </button>

          {showModeSelector && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-primary border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-10">
              <div className="p-2">
                <button
                  onClick={() => {
                    setMode('auto');
                    setShowModeSelector(false);
                  }}
                  className={`w-full text-left p-3 rounded-lg transition-colors ${mode === 'auto' ? 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                >
                  <div className="flex items-center space-x-2">
                    <MessageCircle size={16} />
                    <div>
                      <div className="font-medium">Auto Mode</div>
                      <div className="text-sm text-secondary">Access all materials from your subscribed courses</div>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => {
                    setMode('course');
                    setShowModeSelector(false);
                  }}
                  className={`w-full text-left p-3 rounded-lg transition-colors ${mode === 'course' ? 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                >
                  <div className="flex items-center space-x-2">
                    <Book size={16} />
                    <div>
                      <div className="font-medium">Course Mode</div>
                      <div className="text-sm text-secondary">Focus on materials from a specific course</div>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => {
                    setMode('material');
                    setShowModeSelector(false);
                  }}
                  className={`w-full text-left p-3 rounded-lg transition-colors ${mode === 'material' ? 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                >
                  <div className="flex items-center space-x-2">
                    <FileText size={16} />
                    <div>
                      <div className="font-medium">Material Mode</div>
                      <div className="text-sm text-secondary">Analyze and discuss a specific material</div>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Course Selection for Course Mode */}
        {mode === 'course' && (
          <div className="mt-3">
            <select
              value={selectedCourseId}
              onChange={(e) => setSelectedCourseId(e.target.value)}
              className="w-full p-3 bg-primary border border-gray-300 dark:border-gray-600 rounded-lg text-primary"
            >
              <option value="">Select a course</option>
              {courses.map(course => (
                <option key={course.id} value={course.id}>
                  {course.code} - {course.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Material Search for Material Mode */}
        {mode === 'material' && (
          <div className="mt-3 space-y-2">
            <input
              type="text"
              placeholder="Search materials..."
              value={materialSearchQuery}
              onChange={(e) => {
                setMaterialSearchQuery(e.target.value);
                searchMaterials(e.target.value);
              }}
              className="w-full p-3 bg-primary border border-gray-300 dark:border-gray-600 rounded-lg text-primary placeholder-gray-500 dark:placeholder-gray-400"
            />
            {materials.length > 0 && (
              <div className="max-h-32 overflow-y-auto bg-primary border border-gray-300 dark:border-gray-600 rounded-lg">
                {materials.map(material => (
                  <button
                    key={material.id}
                    onClick={() => {
                      setSelectedMaterialId(material.id);
                      setMaterials([]);
                      setMaterialSearchQuery(material.title);
                    }}
                    className="w-full text-left p-2 hover:bg-gray-100 dark:hover:bg-gray-800 border-b border-gray-200 dark:border-gray-700 last:border-b-0"
                  >
                    <div className="font-medium text-sm">{material.title}</div>
                    <div className="text-xs text-secondary">{material.courseCode} - {material.type}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="text-center text-secondary py-12">
            <MessageCircle size={48} className="mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-medium mb-2">Welcome to Lexi AI!</h3>
            <p className="text-sm">Ask me anything about your course materials. I'm here to help you learn and understand better.</p>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-3xl px-4 py-3 rounded-2xl ${
                  message.sender === 'user'
                    ? 'bg-red-600 text-white ml-12'
                    : message.isError
                    ? 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 mr-12'
                    : 'bg-gray-100 dark:bg-gray-800 text-primary mr-12'
                }`}
              >
                <div className="whitespace-pre-wrap">{message.content}</div>
                {message.contextSources && message.contextSources.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-300 dark:border-gray-600">
                    <div className="text-xs text-secondary mb-2">Sources used:</div>
                    <div className="space-y-1">
                      {message.contextSources.map((source, index) => (
                        <a 
                          key={index} 
                          href={source.materialUrl || `https://edx.red/materials/${source.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block text-xs bg-gray-50 dark:bg-gray-700 p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors group"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors">
                                {source.title}
                              </div>
                              <div className="text-secondary">{source.courseCode} - {source.type}</div>
                            </div>
                            <ExternalLink size={12} className="text-gray-400 group-hover:text-red-500 transition-colors" />
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                <div className="text-xs text-gray-500 mt-2">
                  {new Date(message.timestamp).toLocaleTimeString()}
                </div>
              </div>
            </div>
          ))
        )}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 dark:bg-gray-800 px-4 py-3 rounded-2xl mr-12">
              <div className="flex items-center space-x-2">
                <div className="animate-bounce w-2 h-2 bg-gray-500 rounded-full"></div>
                <div className="animate-bounce w-2 h-2 bg-gray-500 rounded-full" style={{animationDelay: '0.1s'}}></div>
                <div className="animate-bounce w-2 h-2 bg-gray-500 rounded-full" style={{animationDelay: '0.2s'}}></div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

        {/* Input */}
        <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200 dark:border-gray-700 bg-secondary">
          <div className="flex space-x-3">
            <input
              ref={inputRef}
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder="Ask me anything about your courses..."
              className="flex-1 p-3 bg-primary border border-gray-300 dark:border-gray-600 rounded-xl text-primary placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              disabled={isLoading}
            />
            <button
              type="submit"
              disabled={!inputMessage.trim() || isLoading}
              className="p-3 bg-red-600 text-white rounded-xl hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              <Send size={20} />
            </button>
          </div>
        </form>
      </div>

      {/* Upgrade Prompt Modal */}
      <UpgradePrompt
        isVisible={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        title="Upgrade to Pro for AI Chat"
        description="Get unlimited access to Lexi, our AI assistant, for help with your coursework and materials."
      />
    </div>
  );
};

export default ChatPage;